import { Tag } from 'antd'
import { ReactNode } from 'react'
import type { AttendeeStatus } from '@/pages/events/attendees/types'

export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'default' | 'processing'

export interface BadgeProps {
  /**
   * Visual variant of the badge
   * @default 'default'
   */
  variant?: BadgeVariant

  /**
   * Badge content
   */
  children: ReactNode

  /**
   * Show as a dot indicator
   */
  showDot?: boolean

  /**
   * Custom color (overrides variant)
   */
  color?: string
}

/**
 * Badge Component
 *
 * A status indicator component that can be used to show counts, status, or labels.
 *
 * @example
 * // Status badge
 * <Badge variant="success">Active</Badge>
 *
 * @example
 * // Dot indicator badge
 * <Badge variant="error" showDot>
 *   Failed
 * </Badge>
 *
 * @example
 * // Custom color badge
 * <Badge color="#87d068">Custom</Badge>
 */
export function Badge({ variant = 'default', children, showDot = false, color }: BadgeProps) {
  const colorMap: Record<BadgeVariant, string> = {
    success: 'green',
    warning: 'orange',
    error: 'red',
    info: 'blue',
    processing: 'blue',
    default: 'default',
  }

  const dotColorMap: Record<BadgeVariant, string> = {
    success: '#52C41A',
    error: '#FF4D4F',
    warning: '#FAAD14',
    info: '#1890FF',
    processing: '#1890FF',
    default: '#D9D9D9',
  }

  // If custom color is provided, use it
  const tagColor = color || colorMap[variant]
  const dotColor = color || dotColorMap[variant]

  // Dot variant - show colored dot with text
  if (showDot) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: dotColor }}
          aria-hidden="true"
        />
        <span>{children}</span>
      </div>
    )
  }

  // Tag variant - show as colored tag
  return <Tag color={tagColor}>{children}</Tag>
}

/**
 * StatusBadge Component
 *
 * A specialized badge for showing status with a dot indicator.
 * This is a convenience component that always shows a dot.
 *
 * @example
 * <StatusBadge status="live" />
 * <StatusBadge status="draft" />
 */
export interface StatusBadgeProps {
  status: 'live' | 'draft' | 'archived' | 'purged'
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const statusMap: Record<string, { variant: BadgeVariant; label: string }> = {
    live: { variant: 'success', label: 'Live' },
    draft: { variant: 'default', label: 'Draft' },
    archived: { variant: 'info', label: 'Archived' },
    purged: { variant: 'error', label: 'Purged' },
  }

  const config = statusMap[status] || statusMap.draft

  return (
    <Badge variant={config.variant} showDot>
      {config.label}
    </Badge>
  )
}

/**
 * AttendeeStatusBadge Component
 *
 * A specialized badge for showing attendee registration status.
 *
 * @example
 * <AttendeeStatusBadge status="pending" />
 * <AttendeeStatusBadge status="approved" />
 * <AttendeeStatusBadge status="rejected" />
 * <AttendeeStatusBadge status="cancelled" />
 */
export interface AttendeeStatusBadgeProps {
  status: AttendeeStatus
  showDot?: boolean
}

export function AttendeeStatusBadge({ status, showDot = false }: AttendeeStatusBadgeProps) {
  const statusMap: Record<AttendeeStatus, { variant: BadgeVariant; label: string }> = {
    pending: { variant: 'warning', label: 'Pending' },
    approved: { variant: 'success', label: 'Approved' },
    rejected: { variant: 'error', label: 'Rejected' },
    cancelled: { variant: 'default', label: 'Cancelled' },
  }

  const config = statusMap[status] || statusMap.pending

  return (
    <Badge variant={config.variant} showDot={showDot}>
      {config.label}
    </Badge>
  )
}
